/**
 * Material-balance property rows (voronoi-mb PropsTable) + seeding from scenario layer.
 */
import type { ScenarioLayer, VolumetricEstimate } from '@/api/scenarioLayers';
import type { MbPropsOverrides } from '@/types/materialBalance';

export const MB_PROP_ROWS = [
  {
    key: 'ngz_kt',
    label: 'OOIP',
    unit: 'kt',
    hint: 'Original oil in place — initial geological reserves of oil in the layer (thousand tonnes).',
  },
  {
    key: 'so',
    label: 'So',
    unit: 'frac',
    hint: 'Initial oil saturation — fraction of pore volume occupied by oil at start (0–1).',
  },
  {
    key: 'bo',
    label: 'Bo',
    unit: 'm³/m³',
    hint: 'Oil formation volume factor — reservoir volume of oil per unit surface volume.',
  },
  {
    key: 'rho',
    label: 'ρo',
    unit: 't/m³',
    hint: 'Oil density at surface conditions (t/m³; same numeric scale as g/cm³).',
  },
  {
    key: 'mu_o',
    label: 'μo',
    unit: 'cP',
    hint: 'Oil viscosity at reservoir conditions (cP ≡ mPa·s). Affects well productivity.',
  },
  {
    key: 'mu_w',
    label: 'μw',
    unit: 'cP',
    hint: 'Water viscosity at reservoir conditions. Used with oil viscosity for water-cut / mobility.',
  },
  {
    key: 'k_um2',
    label: 'k',
    unit: 'μm²',
    hint: 'Absolute permeability of the rock (μm²; 1 μm² = 1000 mD). Controls Darcy flow to the well.',
  },
  {
    key: 'h_m',
    label: 'h',
    unit: 'm',
    hint: 'Net pay thickness — effective producing thickness of the reservoir (m).',
  },
  {
    key: 'phi',
    label: 'φ',
    unit: 'frac',
    hint: 'Porosity — fraction of rock volume that is pore space (0–1).',
  },
  {
    key: 'pi',
    label: 'Pi',
    unit: 'MPa',
    hint: 'Initial reservoir pressure. Starting tank pressure for material balance.',
  },
  {
    key: 'pwf',
    label: 'Pwf',
    unit: 'MPa',
    hint: 'Bottom-hole flowing pressure. Drawdown (Pi − Pwf) drives liquid rate via productivity index.',
  },
  {
    key: 'q_liq_start_td',
    label: 'Liq rate, start',
    unit: 't/d',
    hint: 'Initial liquid rate of a typical producer (t/d). From the type-well oil curve (thous. t/yr → t/d) and start water cut.',
  },
  {
    key: 'fw_start',
    label: 'WC, start',
    unit: 'frac',
    hint: 'Initial water cut (fraction of liquid that is water, 0–1). Prefer ML wc0 over water saturation when available.',
  },
  {
    key: 'kvyt',
    label: 'E_d',
    unit: 'frac',
    hint: 'Displacement efficiency (microscopic recovery) — fraction of OOIP that can be displaced; caps recoverable oil in the model.',
  },
  {
    key: 'f_inj',
    label: 'f_inj',
    unit: 'frac',
    hint: 'Injection effectiveness — fraction of injected water that supports reservoir pressure in the material-balance tank.',
  },
  {
    key: 'u_aq',
    label: 'U aquifer',
    unit: '',
    hint: 'Aquifer influx strength — extra pressure support from aquifer (same units as elastic storage in the tank model). Higher = stronger water drive.',
  },
  {
    key: 'voidage_ratio',
    label: 'voidage L/I',
    unit: '',
    hint: 'Target liquid-to-injection voidage ratio. When voidage floor is on, liquid rate is kept ≥ ratio × allocated injection.',
  },
  {
    key: 'skin',
    label: 'skin',
    unit: '',
    hint: 'Well skin factor. Positive = near-well damage (lower rate); negative = stimulation (higher rate).',
  },
] as const;

export type MbPropKey = (typeof MB_PROP_ROWS)[number]['key'];

export type MbPropsForm = Record<MbPropKey, number | ''> & {
  liq_voidage: boolean;
};

/** Engine defaults when a field is blank (same spirit as backend DEFAULT_PROPS). */
export const MB_PROP_DEFAULTS: Record<MbPropKey, number> = {
  ngz_kt: 0,
  so: 0.5,
  bo: 1.2,
  rho: 0.85,
  mu_o: 2.0,
  mu_w: 0.5,
  k_um2: 0.1,
  h_m: 10.0,
  phi: 0.2,
  pi: 20.0,
  pwf: 10.0,
  q_liq_start_td: 0,
  fw_start: 0.1,
  kvyt: 0.45,
  f_inj: 0.7,
  u_aq: 500,
  voidage_ratio: 0.95,
  skin: 0,
};

export function emptyMbPropsForm(): MbPropsForm {
  // Voidage floor disabled — liquid from cell MB pressure + IPR only.
  const form = { liq_voidage: false } as MbPropsForm;
  for (const row of MB_PROP_ROWS) {
    form[row.key] = '';
  }
  return form;
}

export function mbPropsFormFromOverrides(
  props: MbPropsOverrides | null | undefined,
  _liqVoidage?: boolean | null,
): MbPropsForm {
  const form = emptyMbPropsForm();
  for (const row of MB_PROP_ROWS) {
    const v = props?.[row.key as keyof MbPropsOverrides];
    if (typeof v === 'number' && Number.isFinite(v)) {
      form[row.key] = roundMbProp(v);
    }
  }
  return form;
}

/** Round numeric MB props to 3 decimal places for display/storage. */
export function roundMbProp(n: number): number {
  return Number(n.toFixed(3));
}

export function mbPropsOverridesFromForm(form: MbPropsForm): MbPropsOverrides & {
  liq_voidage: boolean;
  skin: number;
} {
  const out: Record<string, number | boolean> = {
    // Always off: voidage compensation is via cell tank MB → p → IPR.
    liq_voidage: false,
  };
  for (const row of MB_PROP_ROWS) {
    const v = form[row.key];
    if (v === '' || v == null) continue;
    const n = Number(v);
    if (!Number.isFinite(n)) continue;
    if (row.key === 'q_liq_start_td' && n <= 0) continue;
    out[row.key] = roundMbProp(n);
  }
  return out as MbPropsOverrides & { liq_voidage: boolean; skin: number };
}

/** A start liquid rate of 0 is the unset placeholder, not a value — it must not mask the ML prediction. */
export function hasSavedStartLiquidRate(saved: MbPropsOverrides | null | undefined): boolean {
  const v = saved?.q_liq_start_td;
  return typeof v === 'number' && Number.isFinite(v) && v > 0;
}

function asFraction(v: number): number {
  return v > 1 ? v / 100 : v;
}

function propValue(
  byName: Record<string, number | null>,
  ...names: string[]
): number | null {
  for (const name of names) {
    const v = byName[name];
    if (v != null && Number.isFinite(v)) return Number(v);
  }
  return null;
}

/** Build attribute-name → number map from scenario layer properties. */
export function layerPropertyValueMap(
  layer: ScenarioLayer,
): Record<string, number | null> {
  const map: Record<string, number | null> = {};
  for (const p of layer.properties || []) {
    const name = p.attribute?.name;
    if (!name) continue;
    const val = p.float_value ?? p.integer_value ?? null;
    map[name] = val != null && Number.isFinite(Number(val)) ? Number(val) : null;
  }
  return map;
}

function averageNetPayM(volumetric?: VolumetricEstimate | null): number | null {
  if (!volumetric?.points?.length) return null;
  const vals: number[] = [];
  for (const p of volumetric.points) {
    if (p.gross_thickness_m == null || !Number.isFinite(Number(p.gross_thickness_m))) continue;
    const ntg =
      p.net_to_gross_pct != null && Number.isFinite(Number(p.net_to_gross_pct))
        ? Number(p.net_to_gross_pct) / 100
        : 1;
    vals.push(Number(p.gross_thickness_m) * ntg);
  }
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/**
 * Prefill MB props from reservoirs tab (layer attributes, resource OOIP, recovery factor, volumetric).
 * Only returns keys that can be derived — caller merges under saved config.
 */
export function seedMbPropsFromLayer(
  layer: ScenarioLayer,
  volumetric?: VolumetricEstimate | null,
): Partial<Record<MbPropKey, number>> {
  const byName = layerPropertyValueMap(layer);
  const out: Partial<Record<MbPropKey, number>> = {};

  const ooip =
    layer.resource?.initial_geological ??
    volumetric?.total_geological_thousand_tons ??
    null;
  if (ooip != null && Number.isFinite(Number(ooip))) {
    out.ngz_kt = Number(ooip);
  }

  const so = propValue(
    byName,
    'Oil saturation coefficient',
    'Average effective oil saturation',
  );
  if (so != null) out.so = asFraction(so);

  const bo = propValue(byName, 'Oil formation volume factor');
  if (bo != null) out.bo = bo;

  const rho = propValue(
    byName,
    'Oil density at surface conditions',
    'Oil density at reservoir conditions',
  );
  if (rho != null) out.rho = rho; // g/cm³ ≡ t/m³

  const muO = propValue(byName, 'Oil viscosity at reservoir conditions');
  if (muO != null) out.mu_o = muO; // mPa·s ≡ cP

  const muW = propValue(byName, 'Water viscosity at reservoir conditions');
  if (muW != null) out.mu_w = muW;

  const kMd = propValue(byName, 'Permeability');
  if (kMd != null) out.k_um2 = kMd / 1000; // mD → μm²

  const h = averageNetPayM(volumetric);
  if (h != null) out.h_m = h;

  const phi = propValue(byName, 'Porosity coefficient');
  if (phi != null) out.phi = asFraction(phi);

  const pi = propValue(byName, 'Initial reservoir pressure');
  if (pi != null) {
    out.pi = pi;
    // Reasonable default drawdown if Pwf not set elsewhere
    out.pwf = Math.max(0.5, pi * 0.5);
  }

  const fw = propValue(byName, 'Average effective water saturation');
  if (fw != null) out.fw_start = asFraction(fw);

  const ed = propValue(
    byName,
    'Displacement coefficient',
    'Displacement coefficient (by gas)',
  );
  if (ed != null) {
    out.kvyt = asFraction(ed);
  } else if (layer.recovery_factor != null && Number.isFinite(Number(layer.recovery_factor))) {
    out.kvyt = Number(layer.recovery_factor);
  }

  const densVol = volumetric?.oil_density_g_cm3;
  if (out.rho == null && densVol != null && Number.isFinite(Number(densVol))) {
    out.rho = Number(densVol);
  }

  return out;
}

/** Overlay ML-predicted start liquid rate / WC onto a layer seed (ML wins over Sw). */
export function applyMlStartDefaultsToSeed(
  seed: Partial<Record<MbPropKey, number>>,
  ml: { q_liq_start_td?: number | null; fw_start?: number | null } | null | undefined,
): Partial<Record<MbPropKey, number>> {
  if (!ml) return seed;
  const out = { ...seed };
  if (typeof ml.q_liq_start_td === 'number' && Number.isFinite(ml.q_liq_start_td) && ml.q_liq_start_td > 0) {
    out.q_liq_start_td = ml.q_liq_start_td;
  }
  if (typeof ml.fw_start === 'number' && Number.isFinite(ml.fw_start) && ml.fw_start >= 0) {
    out.fw_start = ml.fw_start;
  }
  return out;
}

/** Merge: defaults ← seed ← saved (saved wins for present keys). */
export function mergeMbPropsForm(
  seed: Partial<Record<MbPropKey, number>>,
  saved: MbPropsOverrides | null | undefined,
  _liqVoidage?: boolean | null,
): MbPropsForm {
  const form = emptyMbPropsForm();

  for (const row of MB_PROP_ROWS) {
    const seeded = seed[row.key];
    if (typeof seeded === 'number' && Number.isFinite(seeded)) {
      form[row.key] = roundMbProp(seeded);
    } else {
      form[row.key] = roundMbProp(MB_PROP_DEFAULTS[row.key]);
    }
  }

  if (saved) {
    for (const row of MB_PROP_ROWS) {
      if (row.key === 'q_liq_start_td' && !hasSavedStartLiquidRate(saved)) continue;
      const v = saved[row.key as keyof MbPropsOverrides];
      if (typeof v === 'number' && Number.isFinite(v)) {
        form[row.key] = roundMbProp(v);
      }
    }
  }

  return form;
}

export function mbPropsFormsEqual(a: MbPropsForm, b: MbPropsForm): boolean {
  return MB_PROP_ROWS.every((row) => {
    const av = a[row.key];
    const bv = b[row.key];
    if (av === '' && bv === '') return true;
    if (av === '' || bv === '') return false;
    return Number(av) === Number(bv);
  });
}

/**
 * True when the form already has a meaningful value for this key.
 * Empty fields and pure engine defaults count as missing (so AI "fill missing"
 * can still fill placeholders after a default seed).
 */
export function mbPropIsOccupied(form: MbPropsForm, key: MbPropKey): boolean {
  const v = form[key];
  if (v === '' || v == null) return false;
  if (typeof v !== 'number' || !Number.isFinite(v)) return false;
  return Number(v) !== MB_PROP_DEFAULTS[key];
}

/** Keys in `suggested` that would overwrite an existing numeric form value. */
export function mbPropsOverwriteKeys(
  form: MbPropsForm,
  suggested: Partial<Record<MbPropKey, number>> | Record<string, number>,
): MbPropKey[] {
  const keys: MbPropKey[] = [];
  for (const row of MB_PROP_ROWS) {
    const v = suggested[row.key];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    if (mbPropIsOccupied(form, row.key)) keys.push(row.key);
  }
  return keys;
}

export type MbPropsAiApplyMode = 'replace' | 'fill_missing';

/** Apply AI-suggested props onto the form (replace all suggested keys, or only empty). */
export function applyMbPropsAiSuggestion(
  form: MbPropsForm,
  suggested: Partial<Record<MbPropKey, number>> | Record<string, number>,
  mode: MbPropsAiApplyMode,
): MbPropsForm {
  const next: MbPropsForm = { ...form };
  for (const row of MB_PROP_ROWS) {
    const v = suggested[row.key];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    if (mode === 'fill_missing' && mbPropIsOccupied(form, row.key)) continue;
    next[row.key] = roundMbProp(v);
  }
  return next;
}

/** Numeric snapshot of current form for sending as assist context. */
export function mbPropsFormToContext(
  form: MbPropsForm,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of MB_PROP_ROWS) {
    const v = form[row.key];
    if (typeof v === 'number' && Number.isFinite(v)) out[row.key] = v;
  }
  return out;
}
